import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { Association, Job, JobTag, JobTagAdapter } from '@shared/models';
import { JobStatusEnum } from '@shared/models/job-statuses';
import { Pagination } from '@shared/pagination';

import { ApiParams, ApiRequest, ApiService, SortDir } from './api.service';

export interface JobsParams extends ApiParams {
  jobAssignedTo?: string;
  status?: JobStatusEnum;
  offset?: number;
  size?: number;
  sort?: string;
  'sort.dir'?: SortDir;
  onlyInProgress?: boolean;
}

@Injectable({ providedIn: 'root' })
export class JobService extends ApiService<Job> {
  constructor(http: HttpClient) {
    super(http);
    this.url = 'job-description/';
  }

  getByRecruiter(request: Partial<ApiRequest<JobsParams>>): Observable<any> {
    const defaultParams: JobsParams = {
      offset: 0,
      size: 10,
    };
    const params = {
      ...defaultParams,
      ...request.params,
    };
    return this.get({ ...request, params });
  }

  getAssociationsHired(id: number): Observable<Association[]> {
    return this.get({
      url: `${id}/associations/hired`,
    });
  }

  getAssociationsByJobIdAndCandidateIds(
    jobId: number,
    candidateIds: number[],
  ): Observable<Association[]> {
    const ids = candidateIds.join(',');
    return this.get({
      url: `${jobId}/association-for-candidates?candidateIds=${ids}`,
    });
  }

  autocomplete(searchPhrase: string, statuses: JobStatusEnum[], size = 20): Observable<JobTag[]> {
    const params = { name: searchPhrase, size } as ApiParams;
    return this.searchJobs(params, statuses?.join(',')).pipe(
      map((jobPage: Pagination<Job>) => jobPage.content),
      map((jobs: Job[]) => jobs.map((job) => JobTagAdapter.toOption(job))),
    );
  }

  searchJobs(apiParams: ApiParams, status?: string, mentionSearch = false): Observable<any> {
    if (!!mentionSearch) {
      return this.get({
        url: '',
        params: { ...apiParams, status } as JobsParams,
      });
    } else {
      return this.get({
        url: '',
        params: { search: 'search', ...apiParams, status } as JobsParams,
      });
    }
  }

  getHiringManagerJobIds(userId: number): Observable<number[]> {
    return this.get({
      url: `hiring-manager/${userId}/ids/list`,
    });
  }

  getHiringManagerJobs(requestParams, userId: number): Observable<Pagination<Job>> {
    const defaultParams: JobsParams = {
      offset: 0,
      size: 10,
    };
    const params = {
      ...defaultParams,
      ...requestParams,
    };

    return this.get({
      url: `hiring-manager/${userId}`,
      params,
    });
  }

  updateTeamMembers(jobId: number, teamMembers) {
    return this.put({
      url: `${jobId}/team-members/`,
      body: teamMembers,
    });
  }

  getDirectPerPhase(jobId: number) {
    return this.get({
      url: `${jobId}/associations?totalDirectPerPhase`,
    });
  }

  getDirectPerPhaseForGivenIds(jobsIds: number[]) {
    const ids = jobsIds.join(',');

    return this.get({
      url: `associations?totalDirectPerPhase&ids=${ids}`,
    });
  }

  getAllActiveJobsForExtendedReferral(jobBoardCanonicalName): Observable<Pagination<Job>> {
    return this.post({
      url: 'board/' + jobBoardCanonicalName,
      params: { offset: 0, size: 1, allJobs: true },
      body: { ids: [], titles: [] },
    });
  }
}
